import {
  VISIT_FORMATS,
  type StorefrontTeam,
  type StorefrontTeamMember,
  type VisitFormat,
} from '@/features/storefront/api/team';
import type {
  BookingHours,
  BookingService,
  BookingsSection,
  Section,
  TeamFilterKind,
  TeamSection,
} from '@/features/storefront/sections/schema';
import { pickedBlocks } from '@/features/storefront/sections/schema';
import { placeholderImage } from '@/features/storefront/sections/placeholderArt';
import { hash } from '@/previewThemes/catalog/artwork';

/**
 * The rules about people that more than one place needs.
 *
 * The team section, the profile page, the bookings section's person picker,
 * and the two shops with no server behind them (a theme's sample shop and the
 * customiser) all ask the same questions - who offers this service, when does
 * this person work, which people does a filter chip leave - and two answers to
 * one question are two things to keep in step with the server. So the answers
 * are here, once, as plain functions.
 */

/**
 * The picture to draw for a person: their photo, else - for a sample person a
 * theme seeded without one - the silhouette the theme's preview drew them
 * with, so the live shop opens looking like the preview it was chosen from.
 * Seeded by their place in `team` (the preview's own order and palette), or a
 * hash of the slug when the list is not at hand. `null` for a merchant's own
 * person without a photo: their initials stand in, as always.
 */
export function memberPhoto(
  member: Pick<StorefrontTeamMember, 'id' | 'slug' | 'photo_url' | 'is_sample'>,
  team?: readonly Pick<StorefrontTeamMember, 'id'>[] | null,
): string | null {
  if (member.photo_url) return member.photo_url;
  if (!member.is_sample) return null;
  const index = team?.findIndex((other) => other.id === member.id) ?? -1;
  return placeholderImage('portrait', index >= 0 ? index : hash(member.slug));
}

/** "AR" for Asha Rao, for a person with no photo yet. Titles like "Dr." are skipped. */
export function initials(name: string): string {
  const words = name
    .replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, '')
    .split(/\s+/)
    .filter(Boolean);
  const letters = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  return letters
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * Every bookable service in a set of page stacks, and the hours a person with
 * no hours of their own works - the first bookings section's, preferring one
 * that books by person. For the profile page.
 *
 * Read from the theme document the shop is already wearing, so the profile
 * page costs no request beyond the person. A service on a page this cannot
 * see (a content page the merchant built separately) is simply not listed - a
 * shorter list, never a wrong one.
 */
export function shopBookings(stacks: readonly Section[][]): {
  services: Pick<BookingService, 'id' | 'name'>[];
  hours: BookingHours[] | null;
} {
  const bookings = stacks
    .flat()
    .filter((section): section is BookingsSection => section.type === 'bookings' && section.visible);
  const services = new Map<string, Pick<BookingService, 'id' | 'name'>>();
  for (const section of bookings) {
    for (const block of pickedBlocks(section)) {
      if (block.visible && block.name.trim() && !services.has(block.id)) {
        services.set(block.id, { id: block.id, name: block.name.trim() });
      }
    }
  }
  const preferred = bookings.find((section) => section.staff_mode !== 'none') ?? bookings[0];
  return { services: [...services.values()], hours: preferred ? preferred.hours : null };
}

/**
 * The services a bookings section can book with this person: its visible,
 * named services, narrowed to the ones they offer when the shopper chooses -
 * a fixed section's one person does everything it lists.
 */
export function memberBookingServices(
  section: BookingsSection,
  member: Pick<StorefrontTeamMember, 'id' | 'service_ids'>,
): BookingService[] {
  if (section.staff_mode === 'none') return [];
  if (section.staff_mode === 'fixed' && section.staff_member_id !== member.id) return [];
  return pickedBlocks(section).filter(
    (block) =>
      block.visible &&
      block.name.trim() !== '' &&
      (section.staff_mode === 'fixed' || offersService(member, block.id)),
  );
}

/**
 * The bookings section a profile embeds as its "Book" block: the first one, in
 * the stacks given (the theme's pages, then the theme's design for the book
 * page), that books by person *and can book this person* - a choose-mode
 * section where they offer something, or a fixed one that is theirs.
 *
 * "The first by-person section" alone would hand a dermatologist's page the
 * calendar of a section fixed on the dentist, so the person is part of the
 * question; `null` - no Book block - when no section can book them.
 */
export function profileBookingSection(
  stacks: readonly Section[][],
  member: Pick<StorefrontTeamMember, 'id' | 'service_ids'>,
): BookingsSection | null {
  for (const section of stacks.flat()) {
    if (section.type !== 'bookings' || !section.visible) continue;
    if (memberBookingServices(section, member).length > 0) return section;
  }
  return null;
}

/** A bookings section's services a shopper can pick: visible and named. */
export function bookableServices(section: BookingsSection): BookingService[] {
  // Only the services the section picked from the catalog (all, with none picked).
  return pickedBlocks(section).filter((block) => block.visible && block.name.trim() !== '');
}

/**
 * The bookings section and service a service's own page books, for the
 * product page's booking card.
 *
 * A service business's services are its products, and its calendar is a
 * bookings section somewhere in the theme - so the page looks, in the same
 * stacks the profile looks in, for a service *named like the product*
 * (case-folded, spaces trimmed): "Teeth whitening" the product books "teeth
 * whitening" the service. Found, that section and that service; not found,
 * the first section with anything to book and no service chosen, so the
 * shopper picks one rather than being started on the wrong appointment.
 * `null` when the theme has nothing to book at all - the page keeps its buy
 * box.
 */
export function serviceBookingSection(
  stacks: readonly Section[][],
  title: string,
): { section: BookingsSection; serviceId: string | null } | null {
  const wanted = title.trim().toLowerCase();
  let first: BookingsSection | null = null;
  for (const section of stacks.flat()) {
    if (section.type !== 'bookings' || !section.visible) continue;
    const services = bookableServices(section);
    if (services.length === 0) continue;
    first ??= section;
    const named = wanted
      ? services.find((service) => service.name.trim().toLowerCase() === wanted)
      : undefined;
    if (named) return { section, serviceId: named.id };
  }
  return first ? { section: first, serviceId: null } : null;
}

/**
 * Every service a theme books, once each by name (case-folded), in page
 * order - what a plan card may book, for the customiser's picker. A service
 * standing in two bookings sections books the same way from either: the
 * `?service=` link is matched by name wherever the form stands.
 */
export function themeBookingServices(stacks: readonly Section[][]): BookingService[] {
  const seen = new Set<string>();
  const services: BookingService[] = [];
  for (const section of stacks.flat()) {
    if (section.type !== 'bookings' || !section.visible) continue;
    for (const service of bookableServices(section)) {
      const key = service.name.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      services.push(service);
    }
  }
  return services;
}

/** `?member=` - what a team card's Book button adds, and what bookings reads. */
export const MEMBER_PARAM = 'member';

/** `?service=` - what a plan card's Book button adds: the service's name, as bookings matches it. */
export const SERVICE_PARAM = 'service';

/**
 * The service a booking form starts on for a `?service=` link: the one named
 * like it (case-folded, spaces trimmed - the rule a product's booking card
 * uses), else none and the form starts as it always did.
 */
export function serviceFromSearch(
  services: readonly Pick<BookingService, 'id' | 'name'>[],
  search?: string | null,
): string | null {
  const value = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  let wanted: string | null = null;
  try {
    wanted = new URLSearchParams(value).get(SERVICE_PARAM);
  } catch {
    return null;
  }
  const name = wanted?.trim().toLowerCase();
  if (!name) return null;
  // By name, as a plan card writes it; or by id, as an account's package
  // "Book a session" link does, which knows its services only by id.
  return (
    services.find((service) => service.name.trim().toLowerCase() === name)?.id ??
    services.find((service) => service.id === wanted?.trim())?.id ??
    null
  );
}

/**
 * `{href}?service={name}` - the booking page a plan card books from, keeping
 * whatever query and `#fragment` the merchant's link already carried.
 */
export function serviceBookHref(href: string, service: string): string {
  const base = href.trim() || '/pages/book';
  const hashAt = base.indexOf('#');
  const path = hashAt >= 0 ? base.slice(0, hashAt) : base;
  const fragment = hashAt >= 0 ? base.slice(hashAt) : '';
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${SERVICE_PARAM}=${encodeURIComponent(service.trim())}${fragment}`;
}

/**
 * Whether a person does this service.
 *
 * An empty list means *every* service: the merchant who has one stylist should
 * not have to tick every box to say "she does everything", and a person added
 * before the services existed should not silently do nothing.
 */
export function offersService(
  member: Pick<StorefrontTeamMember, 'service_ids'>,
  serviceId: string,
): boolean {
  return member.service_ids.length === 0 || member.service_ids.includes(serviceId);
}

/** The people a shopper may pick for a service, in the merchant's order. */
export function eligibleStaff(
  members: readonly StorefrontTeamMember[],
  service: Pick<BookingService, 'id'>,
): StorefrontTeamMember[] {
  return members.filter((member) => offersService(member, service.id));
}

/**
 * Who a bookings section can book, for one service.
 *
 * `none` books nobody in particular; `fixed` is the one named person - or
 * nobody, when they were removed or hidden, which the section shows as no
 * free times rather than inventing a stand-in; `choose` is everyone visible
 * who offers the service.
 */
export function bookableStaff(
  section: Pick<BookingsSection, 'staff_mode' | 'staff_member_id'>,
  service: Pick<BookingService, 'id'>,
  members: readonly StorefrontTeamMember[],
): StorefrontTeamMember[] {
  if (section.staff_mode === 'none') return [];
  if (section.staff_mode === 'fixed') {
    return members.filter((member) => member.id === section.staff_member_id);
  }
  return eligibleStaff(members, service);
}

/** A person's week: their own hours, else the section's. */
export function memberHours(
  section: Pick<BookingsSection, 'hours'>,
  member: Pick<StorefrontTeamMember, 'hours'> | null | undefined,
): BookingHours[] {
  return member?.hours ?? section.hours;
}

/**
 * The week of several people at once, for a calendar strip that has to say
 * which days anybody is in.
 *
 * A weekday is open when any of them is; its hours run from the earliest
 * opening to the latest closing. Only the strip reads it - which times are
 * free is still each person's own grid - so the span is a wide net rather than
 * a promise.
 */
export function combinedHours(weeks: readonly BookingHours[][]): BookingHours[] {
  const rows: BookingHours[] = [];
  for (let weekday = 0; weekday < 7; weekday += 1) {
    const open = weeks
      .map((week) => week.find((row) => row.weekday === weekday))
      .filter((row): row is BookingHours => Boolean(row) && !row?.closed);
    if (open.length === 0) {
      rows.push({ weekday, closed: true, opens: '09:00', closes: '17:00' });
      continue;
    }
    const opens = open.map((row) => row.opens).sort()[0];
    const closes = open.map((row) => row.closes).sort()[open.length - 1];
    rows.push({ weekday, closed: false, opens, closes });
  }
  return rows;
}

/**
 * The specialities the filter chips offer, in the order the people first
 * mention them. Case-folded for matching so "Hair colour" and "hair colour"
 * are one chip, written the way the merchant first wrote it.
 */
export function specialities(members: readonly StorefrontTeamMember[]): string[] {
  const seen = new Map<string, string>();
  for (const member of members) {
    const value = member.speciality?.trim();
    if (value && !seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value);
  }
  return [...seen.values()];
}

/** The people a chip leaves; `null` is "All". */
export function filterBySpeciality(
  members: readonly StorefrontTeamMember[],
  speciality: string | null,
): StorefrontTeamMember[] {
  if (!speciality) return [...members];
  const wanted = speciality.toLowerCase();
  return members.filter((member) => member.speciality?.trim().toLowerCase() === wanted);
}

// --- Search and filters ------------------------------------------------------
/**
 * The filters a team section draws, in order.
 *
 * `filters` when the merchant chose any; else the original speciality chips
 * when the section still carries the older `show_filter` switch - so a
 * section saved before the list existed keeps exactly the chips it had.
 */
export function teamFilterKinds(
  section: Pick<TeamSection, 'filters' | 'show_filter'>,
): TeamFilterKind[] {
  if (section.filters.length > 0) return [...section.filters];
  return section.show_filter ? ['speciality'] : [];
}

/** What the shopper has narrowed the grid to. Every field empty is "everyone". */
export interface TeamFilterState {
  speciality: string | null;
  /** `today`: the first free day is today; `week`: within the next seven days. */
  when: 'today' | 'week' | null;
  /** A weekday (0 = Monday) the person works. */
  weekday: number | null;
  visit_format: VisitFormat | null;
  /** At least this many years. */
  experience: number | null;
  gender: 'female' | 'male' | null;
  /** Case-folded. */
  language: string | null;
  search: string;
}

export const NO_TEAM_FILTER: TeamFilterState = {
  speciality: null,
  when: null,
  weekday: null,
  visit_format: null,
  experience: null,
  gender: null,
  language: null,
  search: '',
};

export function teamFilterActive(state: TeamFilterState): boolean {
  return (
    state.speciality !== null ||
    state.when !== null ||
    state.weekday !== null ||
    state.visit_format !== null ||
    state.experience !== null ||
    state.gender !== null ||
    state.language !== null ||
    state.search.trim() !== ''
  );
}

/** The experience thresholds offered, smallest first. */
export const EXPERIENCE_STEPS = [5, 10, 20] as const;

/** What each filter can offer over a set of people. An empty list hides the filter. */
export interface TeamFilterOptions {
  speciality: string[];
  /** Whether anyone has a next free date (the "today" / "this week" chips). */
  when: Array<'today' | 'week'>;
  weekday: number[];
  visit_format: VisitFormat[];
  experience: number[];
  gender: Array<'female' | 'male'>;
  /** Written the way a person first wrote it; matched case-folded. */
  language: string[];
}

/**
 * Every option each filter can offer, from the people themselves.
 *
 * Only what somebody has is offered - a "Home visit" chip that leaves nobody,
 * or a "20+ years" no one has reached, is a button that empties the page - and
 * "Other" is never a chip: it is the merchant recording a fact, not one a
 * shopper would search a clinic by.
 */
export function teamFilterOptions(members: readonly StorefrontTeamMember[]): TeamFilterOptions {
  const weekdays = new Set<number>();
  const formats = new Set<VisitFormat>();
  const genders = new Set<'female' | 'male'>();
  const languages = new Map<string, string>();
  let maxYears = -1;
  let anyDate = false;
  for (const member of members) {
    member.available_days.forEach((day) => weekdays.add(day));
    member.visit_formats.forEach((format) => formats.add(format));
    if (member.gender === 'female' || member.gender === 'male') genders.add(member.gender);
    for (const language of member.languages) {
      const value = language.trim();
      if (value && !languages.has(value.toLowerCase())) languages.set(value.toLowerCase(), value);
    }
    if (member.experience_years !== null) maxYears = Math.max(maxYears, member.experience_years);
    if (member.next_available_date) anyDate = true;
  }
  const chips = specialities(members);
  return {
    // One speciality is no choice at all, so the row appears from two upward.
    speciality: chips.length > 1 ? chips : [],
    when: anyDate ? ['today', 'week'] : [],
    weekday: [...weekdays].sort((a, b) => a - b),
    visit_format: VISIT_FORMATS.filter((format) => formats.has(format)),
    experience: EXPERIENCE_STEPS.filter((step) => maxYears >= step),
    gender: (['female', 'male'] as const).filter((gender) => genders.has(gender)),
    language: [...languages.values()],
  };
}

/** Whether a kind has anything to offer - the availability filter has two halves. */
export function teamFilterHasOptions(kind: TeamFilterKind, options: TeamFilterOptions): boolean {
  if (kind === 'availability') return options.when.length > 0 || options.weekday.length > 0;
  return options[kind].length > 0;
}

/** `YYYY-MM-DD` for a day `offset` days after `today`, in the browser's calendar. */
export function isoDay(today: Date, offset = 0): string {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
  const month = String(day.getMonth() + 1).padStart(2, '0');
  const date = String(day.getDate()).padStart(2, '0');
  return `${day.getFullYear()}-${month}-${date}`;
}

/**
 * The people the shopper's filters and search leave, in the merchant's order.
 *
 * Every filter narrows (they combine with AND), and a person with nothing
 * stated for a filter that is set is left out by it - "Online" cannot promise
 * someone who never said. Search reads the name, the speciality and the role.
 */
export function filterTeam(
  members: readonly StorefrontTeamMember[],
  state: TeamFilterState,
  today: Date = new Date(),
): StorefrontTeamMember[] {
  const query = state.search.trim().toLowerCase();
  const first = isoDay(today);
  const lastThisWeek = isoDay(today, 6);
  return filterBySpeciality(members, state.speciality).filter((member) => {
    if (query) {
      const haystack = [member.name, member.speciality, member.role]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    if (state.when) {
      const next = member.next_available_date;
      if (!next) return false;
      if (state.when === 'today' && next > first) return false;
      if (state.when === 'week' && next > lastThisWeek) return false;
    }
    if (state.weekday !== null && !member.available_days.includes(state.weekday)) return false;
    if (state.visit_format && !member.visit_formats.includes(state.visit_format)) return false;
    if (state.experience !== null && (member.experience_years ?? -1) < state.experience) {
      return false;
    }
    if (state.gender && member.gender !== state.gender) return false;
    if (
      state.language &&
      !member.languages.some((language) => language.trim().toLowerCase() === state.language)
    ) {
      return false;
    }
    return true;
  });
}

// --- The sidebar's checkboxes -------------------------------------------------
/**
 * What the shopper ticked in the sidebar layout.
 *
 * The top bar's chips are single-choice (`TeamFilterState`); a column of
 * checkboxes invites several ticks in one group, so each group here is a list.
 * The weekday stays one select - "works Monday or Thursday" is not a question
 * anyone asks a clinic - and so does the search box. Every list empty and the
 * rest unset is "everyone".
 */
export interface TeamMultiFilterState {
  speciality: string[];
  when: Array<'today' | 'week'>;
  weekday: number | null;
  visit_format: VisitFormat[];
  experience: number[];
  gender: Array<'female' | 'male'>;
  /** Case-folded. */
  language: string[];
  search: string;
}

export const NO_TEAM_MULTI_FILTER: TeamMultiFilterState = {
  speciality: [],
  when: [],
  weekday: null,
  visit_format: [],
  experience: [],
  gender: [],
  language: [],
  search: '',
};

/** How many boxes are ticked (and the weekday set) - the phone's "Filters (n)". */
export function teamMultiFilterCount(state: TeamMultiFilterState): number {
  return (
    state.speciality.length +
    state.when.length +
    (state.weekday === null ? 0 : 1) +
    state.visit_format.length +
    state.experience.length +
    state.gender.length +
    state.language.length
  );
}

export function teamMultiFilterActive(state: TeamMultiFilterState): boolean {
  return teamMultiFilterCount(state) > 0 || state.search.trim() !== '';
}

/**
 * The people the sidebar's ticks leave, in the merchant's order.
 *
 * Within a group the ticks are alternatives (OR): "Dermatology" and "Dentistry"
 * ticked is anyone who is either. Across groups every group must hold (AND):
 * add "Female" and it is the women among them. An empty group asks nothing.
 * The single-value tests are `filterTeam`'s own, run once per tick, so the two
 * layouts can never disagree about what "Online" or "10+ years" means.
 */
export function filterTeamMulti(
  members: readonly StorefrontTeamMember[],
  state: TeamMultiFilterState,
  today: Date = new Date(),
): StorefrontTeamMember[] {
  // One member against one single-value state: does `filterTeam` keep them?
  const keeps = (member: StorefrontTeamMember, patch: Partial<TeamFilterState>) =>
    filterTeam([member], { ...NO_TEAM_FILTER, ...patch }, today).length === 1;
  const anyOf = <T>(
    member: StorefrontTeamMember,
    values: readonly T[],
    patch: (value: T) => Partial<TeamFilterState>,
  ) => values.length === 0 || values.some((value) => keeps(member, patch(value)));

  return members.filter(
    (member) =>
      keeps(member, { search: state.search, weekday: state.weekday }) &&
      anyOf(member, state.speciality, (speciality) => ({ speciality })) &&
      anyOf(member, state.when, (when) => ({ when })) &&
      anyOf(member, state.visit_format, (visit_format) => ({ visit_format })) &&
      anyOf(member, state.experience, (experience) => ({ experience })) &&
      anyOf(member, state.gender, (gender) => ({ gender })) &&
      anyOf(member, state.language, (language) => ({ language })),
  );
}

// --- Availability on a card ----------------------------------------------------
/**
 * What a card says about when the person is free: `today` when their first
 * free day is today (or, from a stale cache, already past), else the next date
 * itself for the card to format in the shop's language; `null` - say nothing -
 * when the server does not know, rather than guess "fully booked".
 */
export type TeamAvailability = { kind: 'today' } | { kind: 'next'; date: Date } | null;

export function teamAvailability(
  member: Pick<StorefrontTeamMember, 'next_available_date'>,
  today: Date = new Date(),
): TeamAvailability {
  const next = member.next_available_date;
  const match = next ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(next) : null;
  if (!match) return null;
  if (next! <= isoDay(today)) return { kind: 'today' };
  // A calendar day, not an instant: built in the browser's own calendar so
  // "2026-10-14" is the 14th wherever the shopper is.
  return { kind: 'next', date: new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) };
}

/**
 * "Tue, 14 Oct" in the shopper's language (the same parts the profile page's
 * "Next available" line uses); English when the locale is unknown.
 */
export function formatAvailableDate(date: Date, locale: string): string {
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', options).format(date);
  }
}

/** The people a team section draws, before any chip is pressed. */
export function shownMembers(
  section: Pick<TeamSection, 'limit'>,
  team: StorefrontTeam | null,
): StorefrontTeamMember[] {
  const members = team?.members ?? [];
  return section.limit ? members.slice(0, section.limit) : members;
}

/**
 * `{href}?member={slug}`, keeping whatever query the merchant's link already
 * carried and any `#fragment` after it - "/pages/book#calendar" must still
 * land on the calendar.
 */
export function bookHref(href: string, slug: string): string {
  const base = href.trim() || '/pages/book';
  const hashAt = base.indexOf('#');
  const path = hashAt >= 0 ? base.slice(0, hashAt) : base;
  const hash = hashAt >= 0 ? base.slice(hashAt) : '';
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${MEMBER_PARAM}=${encodeURIComponent(slug)}${hash}`;
}

/** Where a person's profile lives, written shopper-side like every section link. */
export function teamMemberHref(slug: string): string {
  return `/team/${encodeURIComponent(slug)}`;
}

/**
 * The `?member=` slug in a query string - the router's, where there is one,
 * else the window's.
 *
 * The bookings section renders in places with no router around it (the
 * customiser, a unit test mounting one section), and the one thing it needs
 * from the address is this; so the caller hands over whichever search it has
 * and a missing one reads as no member.
 */
export function memberFromSearch(search?: string | null): string | null {
  const value = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  try {
    return new URLSearchParams(value).get(MEMBER_PARAM);
  } catch {
    return null;
  }
}
